import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { Camera, Loader2 } from 'lucide-react';

const AvatarUpload = ({ userId, url, fullName, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-generate initials fallback
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const uploadAvatar = async (event) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `avatar-${Date.now()}.${fileExt}`; // using timestamp to prevent caching issues
      const filePath = `${userId}/${fileName}`;

      // 1. Upload to Supabase Storage 'avatars' bucket
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      // 2. Get Public URL
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = publicUrlData.publicUrl;

      // 3. Update profiles table
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);

      if (updateError) {
        throw updateError;
      }

      // 4. Notify parent
      if (onUploadComplete) {
        onUploadComplete(publicUrl);
      }
    } catch (error) {
      alert(`Error uploading avatar: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="relative inline-block">
      <div 
        className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center border-4 border-white shadow-lg group cursor-pointer hover:ring-4 hover:ring-blue-500/30 transition-all duration-300"
        onClick={() => fileInputRef.current?.click()}
      >
        {url ? (
          <img src={url} alt="User Avatar" className="w-full h-full object-cover" />
        ) : (
          <span className="text-3xl sm:text-4xl font-bold text-primary tracking-wider">
            {getInitials(fullName)}
          </span>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full">
          {uploading ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-white mb-1" />
              <span className="text-white text-xs font-medium">Upload</span>
            </>
          )}
        </div>
      </div>
      
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={uploadAvatar}
        disabled={uploading}
      />
    </div>
  );
};

export default AvatarUpload;
